import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
  Drawer,
  Button,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Email as EmailIcon,
  Folder as FolderIcon,
  Security as SecurityIcon,
  AutoAwesome as AIIcon,
  Keyboard as KeyboardIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  Menu as MenuIcon,
  ArrowUpward as TopIcon,
  MailOutline,
  Settings as SettingsIcon,
  Dns as DnsIcon,
  AdminPanelSettings,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

/* ────────────────────────── types ────────────────────────── */

interface DocSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  badge?: string;
  content: React.ReactNode;
}

/* ────────────────────────── sub-components ────────────────── */

const StepItem: React.FC<{ step: number; text: string }> = ({ step, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.6 }}>
    <Box
      sx={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: 0.1,
        bgcolor: '#dc2626', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.75rem', fontWeight: 700,
      }}
    >
      {step}
    </Box>
    <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#334155' }}>{text}</Typography>
  </Box>
);

const TipBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      mt: 2, p: 1.5, borderRadius: 1.5,
      bgcolor: 'rgba(59,130,246,0.06)',
      border: '1px solid rgba(59,130,246,0.15)',
    }}
  >
    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#475569' }}>
      💡 <strong>Tip:</strong> {children}
    </Typography>
  </Box>
);

const SectionH2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem', mt: 3, mb: 1.5, color: '#0f172a' }}>
    {children}
  </Typography>
);

const SectionH3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', mt: 2.5, mb: 1, color: '#0f172a' }}>
    {children}
  </Typography>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.75, fontSize: '0.9rem', color: '#334155' }}>
    {children}
  </Typography>
);

const BulletList: React.FC<{ items: string[]; icon?: 'check' | 'arrow' }> = ({ items, icon = 'check' }) => (
  <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.3 }}>
        {icon === 'check'
          ? <CheckIcon sx={{ fontSize: 18, color: '#16a34a', mt: 0.2, flexShrink: 0 }} />
          : <ArrowIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.3, flexShrink: 0 }} />}
        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: '#334155' }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

const InfoTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
  <Box
    component="table"
    sx={{
      width: '100%', borderCollapse: 'collapse', my: 1.5, fontSize: '0.85rem',
      '& th, & td': {
        border: '1px solid #e2e8f0', px: 1.5, py: 1, textAlign: 'left',
      },
      '& th': {
        bgcolor: '#f8fafc',
        fontWeight: 600, fontSize: '0.85rem', color: '#0f172a',
      },
      '& td': { fontSize: '0.85rem', color: '#334155' },
    }}
  >
    <thead>
      <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
    </thead>
    <tbody>
      {rows.map((row, ri) => (
        <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
      ))}
    </tbody>
  </Box>
);

/* ════════════════════════════ SECTIONS ════════════════════════ */

const buildSections = (navigate: ReturnType<typeof useNavigate>): DocSection[] => [
  /* ─── 1. Getting Started ─── */
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <RocketIcon />,
    badge: 'Start Here',
    content: (
      <Box>
        <SectionH2>Welcome to CruvzMail</SectionH2>
        <Para>
          CruvzMail is your secure, self-hosted email platform built for professionals and organizations.
          Your data stays on your own servers — giving you complete privacy, security, and compliance control.
        </Para>

        <SectionH3>What you can do</SectionH3>
        <BulletList items={[
          'Send and receive emails with rich formatting & attachments',
          'Organize messages with folders, labels, and smart filters',
          'Manage contacts, calendars, tasks, and notes in one place',
          'Use AI-powered tools for drafting and summarizing emails',
          'Track email delivery status in real-time',
          'Customize your experience with themes, signatures, and settings',
        ]} />

        <SectionH2>Signing In</SectionH2>
        <StepItem step={1} text="Open your browser and navigate to your CruvzMail URL (e.g., mail.yourdomain.com)." />
        <StepItem step={2} text="Enter your full email address as the username." />
        <StepItem step={3} text="Type your password and click Sign In." />
        <StepItem step={4} text="You'll land on your Inbox — ready to go!" />

        <TipBox>
          If you forget your password, use the "Forgot Password" link on the login page. Your administrator can also reset it from the admin panel.
        </TipBox>

        <SectionH2>Interface Overview</SectionH2>
        <Para>The left sidebar is your main navigation hub:</Para>
        <InfoTable
          headers={['Section', 'Purpose']}
          rows={[
            ['Mail', 'Access Inbox, Sent, Drafts, and other mail folders'],
            ['Compose', 'Open a new email compose tab'],
            ['Calendar', 'View and manage your schedule'],
            ['Tasks', 'Track to-dos and action items'],
            ['Notes', 'Quick notes and memos'],
            ['Contacts', 'Manage your address book'],
            ['Bookmarks', 'Saved emails for quick access'],
            ['Analytics', 'Email usage statistics'],
            ['AI Assistant', 'AI-powered email help'],
            ['Settings', 'Customize your experience'],
          ]}
        />
      </Box>
    ),
  },

  /* ─── 2. Composing & Sending Email ─── */
  {
    id: 'composing-email',
    title: 'Composing & Sending Email',
    icon: <EmailIcon />,
    content: (
      <Box>
        <SectionH2>Rich Text Editor</SectionH2>
        <Para>
          CruvzMail includes a full-featured rich text editor powered by Tiptap. Press <strong>C</strong> from
          anywhere to open a new compose tab — multiple compose tabs can be open simultaneously.
        </Para>
        <BulletList icon="arrow" items={[
          'Bold, Italic, Strikethrough, Underline',
          'Font family and font size selection',
          'Text color and highlight color',
          'Bullet lists, numbered lists, and block quotes',
          'Links, inline images, and horizontal rules',
          'Code blocks',
        ]} />

        <SectionH2>Reply, Reply All & Forward</SectionH2>
        <InfoTable
          headers={['Action', 'Behavior']}
          rows={[
            ['Reply', 'Respond to the sender only. Subject prefixed with "Re:"'],
            ['Reply All', 'Respond to sender + all To/Cc recipients'],
            ['Forward', 'Send the email to new recipients. Subject prefixed with "Fwd:"'],
            ['Edit as New', 'Open a fresh compose with original To, Cc, subject, and body — no prefix'],
          ]}
        />
        <Para>
          When replying, your signature is placed <strong>above</strong> the quoted content, following standard business email convention.
        </Para>

        <SectionH2>Working with Attachments</SectionH2>
        <InfoTable
          headers={['Limit', 'Value']}
          rows={[
            ['Maximum file size', '25 MB per file'],
            ['Maximum files per email', '10'],
            ['Maximum total size', '25 MB per message'],
          ]}
        />
        <Para>
          Click the paperclip icon or drag-and-drop files into the compose area. Supported formats include
          PDF, DOCX, XLSX, images (JPG/PNG/GIF/SVG), ZIP, and more.
        </Para>

        <SectionH2>Email Signatures</SectionH2>
        <StepItem step={1} text="Go to Settings → Signature." />
        <StepItem step={2} text="Use the rich editor to build your signature with text, images, and links." />
        <StepItem step={3} text="Click Save. It auto-appears on new emails and above quoted content on replies." />
        <TipBox>
          Keep signatures concise (4-6 lines). Include name, role, phone, and website. Use logos at a reasonable size (under 200px wide).
        </TipBox>

        <SectionH2>Draft Management</SectionH2>
        <BulletList items={[
          'Compose tabs auto-save — close without sending and the email moves to Drafts',
          'Open your Drafts folder and click any draft to continue editing',
          'Multiple compose tabs auto-save independently',
        ]} />
      </Box>
    ),
  },

  /* ─── 3. Organization & Folders ─── */
  {
    id: 'organization',
    title: 'Organization & Folders',
    icon: <FolderIcon />,
    content: (
      <Box>
        <SectionH2>System Folders</SectionH2>
        <InfoTable
          headers={['Folder', 'Purpose']}
          rows={[
            ['Inbox', 'Where all incoming messages arrive'],
            ['Sent', 'Copies of every email you send'],
            ['Drafts', 'Unfinished emails saved for later'],
            ['Starred', 'Emails marked with a star'],
            ['Spam', 'Messages flagged as unwanted'],
            ['Trash', 'Deleted messages — auto-purged after 30 days'],
            ['Archive', 'Messages removed from inbox but preserved'],
          ]}
        />

        <SectionH2>Labels</SectionH2>
        <Para>
          Labels let you categorize emails without moving them. A single email can have multiple labels.
          Manage labels in Settings → Labels — create, rename, delete, and assign colors.
        </Para>

        <SectionH2>Stars, Archive & Search</SectionH2>
        <BulletList items={[
          'Click the star icon (☆) on any email row, or press S while viewing',
          'Archive removes emails from Inbox without deleting — press E or use the Archive button',
          'Search across all folders including Archive and Sent with real-time results',
          'Bulk actions: select multiple emails for Delete, Archive, Move, Mark as read/unread',
        ]} />
      </Box>
    ),
  },

  /* ─── 4. Security & 2FA ─── */
  {
    id: 'security',
    title: 'Security & 2FA',
    icon: <SecurityIcon />,
    content: (
      <Box>
        <SectionH2>Authentication</SectionH2>
        <Para>
          CruvzMail uses JWT-based authentication with automatic token rotation and Redis-backed session storage.
          All connections use TLS 1.2+ with Let's Encrypt certificates. Data is encrypted at rest and in transit.
        </Para>

        <SectionH2>Two-Factor Authentication</SectionH2>
        <Para>
          CruvzMail supports SMS and TOTP (Google Authenticator, Authy) two-factor authentication with backup codes.
          Admins can enforce org-wide 2FA for all users.
        </Para>
        <StepItem step={1} text="Go to Settings → Security." />
        <StepItem step={2} text="Click Enable Two-Factor Authentication." />
        <StepItem step={3} text="Choose SMS or Authenticator App (TOTP)." />
        <StepItem step={4} text="Follow the prompts to verify and save backup codes." />

        <SectionH2>Security Features</SectionH2>
        <InfoTable
          headers={['Feature', 'Details']}
          rows={[
            ['Rate Limiting', 'Intelligent rate limiting on API requests, login attempts, and email sending'],
            ['Spam Protection', 'Rspamd-powered spam filtering with quarantine, auto-learning, and customizable rules'],
            ['Session Control', 'Configurable timeouts, active session management, IP restrictions, and geo-blocking'],
            ['Encryption', 'TLS 1.2+ enforced, Let\'s Encrypt certificates, data encrypted at rest and in transit'],
            ['Password Policies', 'Minimum length, complexity requirements, and password expiry configurable by admin'],
            ['Audit Logging', 'All authentication events and admin actions are logged for compliance'],
          ]}
        />
      </Box>
    ),
  },

  /* ─── 5. Email Client Setup ─── */
  {
    id: 'email-client',
    title: 'Email Client Setup',
    icon: <MailOutline />,
    badge: 'Popular',
    content: (
      <Box>
        <SectionH2>Server Settings</SectionH2>
        <Para>
          Use these settings to configure any email client. Your username is your full email address
          (e.g., user@yourdomain.com) and your regular CruvzMail password.
        </Para>
        <InfoTable
          headers={['Protocol', 'Server', 'Port', 'Security']}
          rows={[
            ['IMAP', 'mail.cruvzmail.com', '993', 'SSL/TLS'],
            ['SMTP', 'mail.cruvzmail.com', '587', 'STARTTLS'],
            ['SMTP (Alt)', 'mail.cruvzmail.com', '465', 'SSL/TLS'],
            ['POP3', 'mail.cruvzmail.com', '995', 'SSL/TLS'],
          ]}
        />

        <SectionH2>Supported Clients</SectionH2>
        <InfoTable
          headers={['Client', 'Platform', 'Auto-Setup']}
          rows={[
            ['Microsoft Outlook', 'Windows / macOS', 'autodiscover CNAME'],
            ['Mozilla Thunderbird', 'Windows / macOS / Linux', 'autoconfig CNAME'],
            ['Apple Mail', 'macOS / iOS', 'IMAP 993 + SMTP 587'],
            ['Gmail App / K-9 Mail', 'Android', 'IMAP 993 + SMTP 587'],
            ['Any IMAP/POP3 Client', 'All platforms', 'SRV record auto-discovery'],
          ]}
        />

        <TipBox>
          For auto-discovery, ask your admin to set up <strong>autodiscover</strong> and <strong>autoconfig</strong> CNAME
          records pointing to mail.cruvzmail.com. This lets Outlook and Thunderbird configure themselves automatically.
        </TipBox>
      </Box>
    ),
  },

  /* ─── 6. DNS & Domain Setup ─── */
  {
    id: 'dns-domain',
    title: 'DNS & Domain Setup',
    icon: <DnsIcon />,
    badge: 'Admin',
    content: (
      <Box>
        <SectionH2>Required DNS Records</SectionH2>
        <Para>
          After adding your domain in the CruvzMail admin panel, you need to configure the following
          DNS records with your domain registrar (GoDaddy, Cloudflare, Namecheap, etc.).
        </Para>
        <InfoTable
          headers={['Record', 'Type', 'Purpose']}
          rows={[
            ['MX', 'Mail Exchange', 'Routes incoming email to CruvzMail servers'],
            ['SPF', 'TXT', 'Authorizes CruvzMail to send on behalf of your domain'],
            ['DKIM', 'TXT', 'Adds a digital signature to verify email authenticity'],
            ['DMARC', 'TXT', 'Tells receivers how to handle SPF/DKIM failures'],
          ]}
        />
        <Para>
          Detailed record values are provided in your admin panel under <strong>Domains → DNS Records</strong> after
          you add your domain. Copy and paste them exactly into your DNS provider.
        </Para>

        <SectionH2>Domain Verification</SectionH2>
        <StepItem step={1} text="Log in to the Admin Panel and go to Domains → Add Domain." />
        <StepItem step={2} text="Enter your domain name and click Add." />
        <StepItem step={3} text="Copy the TXT verification record shown and add it to your DNS." />
        <StepItem step={4} text="Click Verify. DNS changes may take up to 24-48 hours to propagate." />

        <TipBox>
          Use a DNS checker tool (e.g., MXToolbox) to verify your records are live before clicking Verify in the admin panel.
        </TipBox>
      </Box>
    ),
  },

  /* ─── 7. Admin Panel ─── */
  {
    id: 'admin-panel',
    title: 'Admin Panel',
    icon: <AdminPanelSettings />,
    badge: 'Admin',
    content: (
      <Box>
        <SectionH2>Overview</SectionH2>
        <Para>
          The admin panel is available at <strong>/admin</strong> for users with admin privileges.
          It provides complete control over your CruvzMail organization.
        </Para>

        <SectionH2>Admin Capabilities</SectionH2>
        <InfoTable
          headers={['Module', 'Capabilities']}
          rows={[
            ['Users', 'Create, edit, disable, delete users. Reset passwords, assign roles'],
            ['Domains', 'Add/remove domains, view DNS records, verify domain ownership'],
            ['Groups & Aliases', 'Create distribution groups and email aliases'],
            ['Spam & Quarantine', 'Review quarantined emails, adjust spam filter sensitivity'],
            ['Branding', 'Customize logo, colors, company name, and login page'],
            ['Billing', 'Manage plans, view usage, handle Razorpay subscriptions'],
            ['Settings', 'Rate limits, attachment policies, session timeouts, feature flags'],
            ['Monitoring', 'Server health, email delivery stats, and audit logs'],
          ]}
        />

        <SectionH2>Email Migration</SectionH2>
        <Para>
          Migrate from Gmail, Outlook, or any IMAP-compatible provider. CruvzMail preserves folder structure,
          labels, read/unread status, and original message dates.
        </Para>
        <StepItem step={1} text="Go to Admin Panel → Migration." />
        <StepItem step={2} text="Select the source provider (Gmail, Outlook, or Custom IMAP)." />
        <StepItem step={3} text="Enter the source server credentials." />
        <StepItem step={4} text="Map folders and start the migration. Progress is shown in real-time." />
      </Box>
    ),
  },

  /* ─── 8. Productivity Features ─── */
  {
    id: 'productivity',
    title: 'Productivity Features',
    icon: <AIIcon />,
    content: (
      <Box>
        <SectionH2>Built-in Tools</SectionH2>
        <Para>
          CruvzMail includes a full suite of productivity tools accessible from the sidebar — no need for
          separate apps.
        </Para>

        <InfoTable
          headers={['Feature', 'Description']}
          rows={[
            ['Calendar', 'View daily, weekly, or monthly schedules. Create events and meetings directly from email'],
            ['Tasks', 'Track to-dos with priority levels and due dates. Convert any email into a task'],
            ['Contacts', 'Built-in address book with auto-complete, custom groups, and email history per contact'],
            ['Notes', 'Quick notes for meetings, references, and ideas. Supports rich text formatting'],
            ['Bookmarks', 'Save important emails for quick reference — separate from starring'],
            ['AI Assistant', 'Draft emails, summarize threads, rewrite tone, generate subjects, and translate'],
          ]}
        />

        <SectionH2>AI Assistant</SectionH2>
        <Para>
          The AI Assistant is available in the sidebar and within the compose editor.
          It can help you draft emails, summarize long threads, adjust tone (formal, friendly, concise),
          generate subject lines, and translate messages.
        </Para>

        <SectionH2>Auto-Reply (Out of Office)</SectionH2>
        <Para>
          Go to Settings → Auto-Reply to set up an automatic response when you're away. You can set a date
          range and customize the reply message.
        </Para>
      </Box>
    ),
  },

  /* ─── 9. Keyboard Shortcuts ─── */
  {
    id: 'shortcuts',
    title: 'Keyboard Shortcuts',
    icon: <KeyboardIcon />,
    content: (
      <Box>
        <SectionH2>Available Shortcuts</SectionH2>
        <Para>
          These shortcuts work when you're not typing in a text field. They help you navigate and manage
          emails faster.
        </Para>
        <InfoTable
          headers={['Shortcut', 'Action']}
          rows={[
            ['C', 'Open a new compose tab'],
            ['/', 'Focus the search bar'],
            ['R', 'Reply to the current email'],
            ['A', 'Reply all'],
            ['F', 'Forward the current email'],
            ['E', 'Archive selected emails'],
            ['#', 'Delete selected emails (move to Trash)'],
            ['S', 'Toggle star on selected email'],
            ['U', 'Mark selected email as unread'],
            ['Esc', 'Close current panel / Go back'],
          ]}
        />
        <TipBox>
          You can view this list anytime from Settings → Keyboard Shortcuts, or by pressing <strong>?</strong> from any screen.
        </TipBox>
      </Box>
    ),
  },

  /* ─── 10. Configuration & Limits ─── */
  {
    id: 'configuration',
    title: 'Configuration & Limits',
    icon: <SettingsIcon />,
    content: (
      <Box>
        <SectionH2>Send & Rate Limits</SectionH2>
        <InfoTable
          headers={['Limit', 'Value']}
          rows={[
            ['Daily send limit per user', '500 emails'],
            ['Hourly send limit per user', '100 emails'],
            ['Max recipients per email', '100'],
            ['Max attachment size', '25 MB per file'],
            ['Max attachments per email', '10'],
          ]}
        />

        <SectionH2>Session & Account</SectionH2>
        <InfoTable
          headers={['Setting', 'Default']}
          rows={[
            ['Session timeout', '24 hours (configurable by admin)'],
            ['Failed login lockout', '5 attempts, then 15-minute lockout'],
            ['Password minimum length', '8 characters (configurable)'],
            ['Trash auto-purge', '30 days'],
          ]}
        />

        <SectionH2>Need Help?</SectionH2>
        <Para>
          If you need help with setup, migration, or any other topic, our support team is ready to assist.
        </Para>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={() => navigate('/contact')}
            sx={{
              bgcolor: '#dc2626', px: 3, py: 1, borderRadius: '8px', fontSize: '0.85rem',
              fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#b91c1c' },
            }}
          >
            Contact Support
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/mail/help')}
            sx={{
              borderColor: '#e2e8f0', color: '#475569', px: 3, py: 1, borderRadius: '8px',
              fontSize: '0.85rem', fontWeight: 600, textTransform: 'none',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626' },
            }}
          >
            Open Help Center
          </Button>
        </Box>
      </Box>
    ),
  },
];

/* ════════════════════════════ PAGE ════════════════════════════ */

const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [activeSection, setActiveSection] = useState(() => searchParams.get('section') || 'getting-started');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const sections = buildSections(navigate);
  const activeContent = sections.find((s) => s.id === activeSection);

  const scrollToTop = () => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    setDrawerOpen(false);
    scrollToTop();
  };

  /* ── Sidebar navigation ── */
  const navContent = (
    <Box sx={{ py: 1 }}>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#dc2626', letterSpacing: '-0.02em' }}>
          CruvzMail
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.25 }}>
          Documentation
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
      <List dense disablePadding>
        {sections.map((s) => (
          <ListItemButton
            key={s.id}
            selected={activeSection === s.id}
            onClick={() => handleSectionClick(s.id)}
            sx={{
              mx: 1, borderRadius: 1, mb: 0.25,
              '&.Mui-selected': {
                bgcolor: 'rgba(220,38,38,0.08)',
                '&:hover': { bgcolor: 'rgba(220,38,38,0.12)' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: activeSection === s.id ? '#dc2626' : '#94a3b8' }}>
              {s.icon}
            </ListItemIcon>
            <ListItemText
              primary={s.title}
              primaryTypographyProps={{
                fontSize: '0.83rem',
                fontWeight: activeSection === s.id ? 600 : 400,
                color: activeSection === s.id ? '#dc2626' : '#334155',
              }}
            />
            {s.badge && (
              <Chip
                label={s.badge}
                size="small"
                sx={{
                  height: 20, fontSize: '0.65rem', fontWeight: 600,
                  bgcolor: s.badge === 'Start Here' ? '#dc2626' : s.badge === 'Admin' ? '#fef9c3' : '#f0fdf4',
                  color: s.badge === 'Start Here' ? '#fff' : s.badge === 'Admin' ? '#b45309' : '#16a34a',
                }}
              />
            )}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <PublicLayout>
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 80px)', bgcolor: '#fff' }}>
        {/* Sidebar — desktop */}
        {!isMobile && (
          <Paper
            elevation={0}
            sx={{
              width: 260, flexShrink: 0, borderRight: '1px solid #e2e8f0',
              overflow: 'auto', bgcolor: '#fff', pt: '80px',
            }}
          >
            {navContent}
          </Paper>
        )}

        {/* Sidebar — mobile drawer */}
        {isMobile && (
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 260 } }}>
            {navContent}
          </Drawer>
        )}

        {/* Main content area */}
        <Box ref={contentRef} sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff', pt: '80px' }}>
          {/* Mobile header bar */}
          {isMobile && (
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
              <IconButton size="small" onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0f172a' }}>{activeContent?.title}</Typography>
            </Box>
          )}

          <Box sx={{ maxWidth: 780, mx: 'auto', p: { xs: 2, sm: 3.5 } }}>
            {/* Section title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Box sx={{ color: '#dc2626' }}>{activeContent?.icon}</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: '#0f172a' }}>
                {activeContent?.title}
              </Typography>
              {activeContent?.badge && (
                <Chip
                  label={activeContent.badge}
                  size="small"
                  sx={{
                    height: 22, fontSize: '0.7rem', fontWeight: 600,
                    bgcolor: activeContent.badge === 'Start Here' ? '#dc2626' : activeContent.badge === 'Admin' ? '#fef9c3' : '#f0fdf4',
                    color: activeContent.badge === 'Start Here' ? '#fff' : activeContent.badge === 'Admin' ? '#b45309' : '#16a34a',
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Section content */}
            {activeContent?.content}

            {/* Navigation footer */}
            <Divider sx={{ mt: 4, mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 4 }}>
              {(() => {
                const idx = sections.findIndex((s) => s.id === activeSection);
                const prev = idx > 0 ? sections[idx - 1] : null;
                const next = idx < sections.length - 1 ? sections[idx + 1] : null;
                return (
                  <>
                    {prev ? (
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: '#dc2626', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => handleSectionClick(prev.id)}
                      >
                        ← {prev.title}
                      </Typography>
                    ) : <Box />}
                    {next ? (
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: '#dc2626', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => handleSectionClick(next.id)}
                      >
                        {next.title} →
                      </Typography>
                    ) : <Box />}
                  </>
                );
              })()}
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', pb: 3, color: '#94a3b8' }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                © {new Date().getFullYear()} CruvzMail. All rights reserved.
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5, opacity: 0.7 }}>
                Questions? Contact support@cruvzmail.com
              </Typography>
            </Box>
          </Box>

          {/* Scroll to top button */}
          <Tooltip title="Back to top">
            <IconButton
              onClick={scrollToTop}
              sx={{
                position: 'fixed', bottom: 24, right: 24,
                bgcolor: '#dc2626', color: '#fff',
                '&:hover': { bgcolor: '#b91c1c' },
                boxShadow: 3,
              }}
            >
              <TopIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default DocumentationPage;
